// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — host authority, the clock, and the host takeover.
//
// Host-authoritative star, with genuinely hidden hands:
//   host  -> all : 'snap'   the public state. No hand, no deck order, no set-aside rank.
//   host  -> one : 'priv'   your own cards, and only what you have legitimately learned.
//   peer  -> host: 'act'    play / back
//   host* -> all : 'htake'  a new host asks everyone to attest their OWN hand
//   peer  -> host*: 'hgive' my hand
//
// THE DRAW PILE SURVIVES A HOST LEAVING, which is the part worth reading.
//
// The fleet's previous hidden-info game could only recover because it had no draw pile at all: it
// dealt the whole deck into hands, so every secret had an owner who could re-attest it, and the note
// that came out of that build says a future game with a face-down pile must "design it out, or accept
// the round ends there". Hearsay has a pile and does neither, because the pile is not actually a
// secret — it is a REMAINDER.
//
// The deck composition is public and fixed. Every discard is public. Every open burn is public. Every
// survivor knows their own hand and can attest it. So:
//
//     pile + set-aside  ==  composition - discards - open burns - attested hands
//
// which the promoted peer can compute exactly. The only thing genuinely lost is the ORDER of those
// cards — and the order was never known to anybody, so re-shuffling it changes nothing observable
// and cannot advantage or disadvantage a single player. A hearing therefore continues through a host
// leaving with the same cards still to come, which is why nothing in this file has to end a round
// early. tests/takeover.test.ts proves the remainder is reconstructed exactly, card for card.

import { makeRng, shuffle } from '@ben-gy/game-engine/rng';
import { chooseBack, choose, tierOf, type TierName } from './bot';
import {
  awards, back, canBack, closeIfCollapsed, deal, forfeit, play, playableCards, playableFrom, privateView, publicView, seatOf,
  specSize, type Award, type Card, type GameState, type PrivateView, type PublicState, type SeatSpec,
} from './game';
import { modeOf, type Mode, type ModeId } from './modes';

export type Action =
  | { t: 'play'; cardId: number; target: string | null; named: number | null }
  | { t: 'back'; on: string };

export const CHANNELS = {
  snap: 'snap',
  priv: 'priv',
  act: 'act',
  handRequest: 'htake',
  handReply: 'hgive',
} as const;

/** A peer's re-attested hand. Ranks and ids, so a trade in flight keeps its identity. */
export interface HandAttest {
  hand: Card[];
}

export interface Transport {
  sendSnap(pub: PublicState): void;
  sendPriv(to: string, priv: PrivateView): void;
  sendAct(a: Action): void;
  requestHands(): void;
  sendHand(to: string, attest: HandAttest): void;
}

const noopTransport: Transport = {
  sendSnap() {},
  sendPriv() {},
  sendAct() {},
  requestHands() {},
  sendHand() {},
};

export interface SessionOpts {
  selfId: string;
  solo: boolean;
  transport?: Transport;
  /** Solo only: the tier every other seat is played at. */
  botTier?: TierName;
  roster?: readonly string[];
  modeId: ModeId;
  round: number;
  standing: Record<string, number>;
  onPublic: (pub: PublicState) => void;
  onPrivate: (priv: PrivateView | null) => void;
  onFlash?: (msg: string) => void;
  onStranded?: () => void;
  onRoundOver?: (awards: Award[]) => void;
  now?: () => number;
  botDelayMs?: number;
  decisionMs?: number;
}

/** How long a promoted host waits for attestations before assuming the silent are gone. */
const ATTEST_TIMEOUT_MS = 4_000;
/** How long a human may sit on a decision in a networked hearing before it is made for them. */
const DECISION_MS = 45_000;
/** The tier that plays for a seat nobody is driving. Fixed, so every peer would compute the same. */
export const FILL_TIER: TierName = 'counsel';

export class Session {
  readonly selfId: string;
  readonly mode: Mode;
  private solo: boolean;
  private tx: Transport;
  private opts: SessionOpts;
  private now: () => number;
  private roster: readonly string[];
  private botTier: TierName;
  private botDelay: number;
  private decisionMs: number;

  private state: GameState | null = null;
  private pub: PublicState | null = null;
  private priv: PrivateView | null = null;
  private isHost = false;
  private ended = false;

  private actorSince = 0;
  private lastActor: string | null = null;
  private botAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  private awaitingHands = false;
  private replies = new Map<string, HandAttest>();
  private attestDeadline = 0;
  private gone = new Set<string>();

  constructor(o: SessionOpts) {
    this.opts = o;
    this.selfId = o.selfId;
    this.solo = o.solo;
    this.tx = o.transport ?? noopTransport;
    this.now = o.now ?? ((): number => Date.now());
    this.roster = o.roster ?? [];
    this.mode = modeOf(o.modeId);
    this.botTier = o.botTier ?? FILL_TIER;
    this.botDelay = o.botDelayMs ?? 850;
    this.decisionMs = o.decisionMs ?? DECISION_MS;
  }

  // ── lifecycle ───────────────────────────────────────────────────────────────

  start(): void {
    if (!this.timer) this.timer = setInterval(() => this.tick(), 200);
  }
  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
  destroy(): void {
    this.stop();
  }

  startAsHost(seed: number, specs: readonly SeatSpec[]): void {
    this.isHost = true;
    this.state = deal(seed, this.mode.id, specs);
    this.lastActor = this.state.turn;
    this.actorSince = this.now();
    this.botAt = this.now() + this.botDelay;
    this.publish();
    this.start();
  }

  /** Who should run this hearing given who is in the room. */
  authorityFor(peers: readonly string[], host?: string | null): string | null {
    const seated = new Set(this.seatIds());
    const eligible = peers.filter((p) => seated.has(p)).sort();
    if (host && eligible.includes(host)) return host;
    return eligible[0] ?? null;
  }

  private seatIds(): string[] {
    if (this.roster.length) return [...this.roster];
    return (this.state?.seats ?? this.pub?.seats ?? []).map((s) => s.id);
  }

  // ── receiving ───────────────────────────────────────────────────────────────

  onSnapshot(pub: PublicState): void {
    if (this.isHost) return;
    this.pub = pub;
    this.opts.onPublic(pub);
    this.checkEnd(pub);
  }
  onPrivate(priv: PrivateView): void {
    this.priv = priv;
    this.opts.onPrivate(priv);
  }

  act(a: Action): void {
    if (this.isHost) this.applyAction(this.selfId, a);
    else this.tx.sendAct(a);
  }
  onAction(from: string, a: Action): void {
    if (this.isHost) this.applyAction(from, a);
  }

  /**
   * The action is always attributed to its SENDER, never to a seat packed into the payload.
   * A sibling game executed whatever seat the message named, which let one peer drive the table.
   */
  private applyAction(from: string, a: Action): void {
    if (!this.state || this.state.phase !== 'play') return;
    const before = this.state;
    if (a.t === 'play') {
      this.state = play(this.state, from, { cardId: a.cardId, target: a.target, named: a.named });
    } else {
      this.state = back(this.state, from, a.on);
    }
    if (this.state !== before) this.afterChange();
  }

  private afterChange(): void {
    if (!this.state) return;
    if (this.state.turn !== this.lastActor) {
      this.lastActor = this.state.turn;
      this.actorSince = this.now();
      this.botAt = this.now() + this.botDelay;
    }
    this.publish();
  }

  // ── roster and the takeover ─────────────────────────────────────────────────

  onPeerLeave(id: string): void {
    this.gone.add(id);
    if (this.awaitingHands && this.haveAll()) this.assumeControl();
    if (!this.isHost || !this.state) return;
    const seat = seatOf(this.state, id);
    if (!seat || !seat.alive) return;
    this.state = forfeit(this.state, id);
    this.afterChange();
  }

  onRoster(): void {
    if (this.isHost) this.publish();
  }

  setHost(isHost: boolean): void {
    if (isHost === this.isHost) return;
    if (!isHost) {
      this.isHost = false;
      this.state = null;
      return;
    }
    if (!this.seatIds().includes(this.selfId) && this.seatIds().length) return;
    this.isHost = true;
    if (this.state) {
      this.publish();
      return;
    }
    if (this.pub?.phase === 'over') {
      this.opts.onFlash?.("You're running the room now.");
      return;
    }
    if (!this.pub) {
      // Promoted before a single snapshot arrived: there is nothing to take over. Never freeze.
      this.isHost = false;
      this.opts.onFlash?.('The host left before the hearing reached you — back to the lobby.');
      this.opts.onStranded?.();
      return;
    }
    this.awaitingHands = true;
    this.replies = new Map();
    if (this.priv) this.replies.set(this.selfId, { hand: this.priv.hand.map((c) => ({ ...c })) });
    this.attestDeadline = this.now() + ATTEST_TIMEOUT_MS;
    this.tx.requestHands();
    this.opts.onFlash?.('The host left — you have the papers now.');
    this.start();
    // If there is nobody left to hear from, take over NOW rather than sitting out the deadline. It
    // matters in the commonest case there is: a two-player room whose host closed the tab, where the
    // survivor is the only seat and would otherwise stare at a dead board for four seconds. Whether
    // onPeerLeave arrives before or after the promotion is a race, so this cannot be left to it.
    if (this.haveAll()) this.assumeControl();
  }

  onHandRequest(from: string): void {
    if (!this.priv) return;
    this.tx.sendHand(from, { hand: this.priv.hand.map((c) => ({ ...c })) });
  }
  onHandReply(from: string, attest: HandAttest): void {
    if (!this.awaitingHands) return;
    this.replies.set(from, attest);
    if (this.haveAll()) this.assumeControl();
  }
  private haveAll(): boolean {
    if (!this.pub) return false;
    return this.pub.seats
      .filter((s) => s.alive && !this.gone.has(s.id) && s.id !== this.selfId)
      .every((s) => this.replies.has(s.id));
  }

  /**
   * Rebuild the authoritative hearing from the last snapshot plus the attested hands, and
   * RECONSTRUCT the draw pile as the remainder of the deck. See the note at the top of this file.
   */
  private assumeControl(): void {
    if (!this.pub) return;
    this.awaitingHands = false;
    const pub = this.pub;
    const attested = new Set(this.replies.keys());

    const seats = pub.seats.map((s) => {
      const missing = !attested.has(s.id) && s.id !== this.selfId;
      const hand = this.replies.get(s.id)?.hand ?? (s.id === this.selfId ? (this.priv?.hand ?? []) : []);
      return {
        id: s.id,
        name: s.name,
        bot: s.bot,
        // Anybody who did not answer is gone; we cannot conjure a hand, and inventing one would be
        // the only genuinely unfair thing this whole protocol could do.
        hand: missing || !s.alive ? [] : hand.map((c) => ({ ...c })),
        discards: s.discards.slice(),
        alive: s.alive && !missing,
        shielded: s.shielded,
        backed: s.backed,
        outCause: s.outCause,
        outPly: s.outPly,
      };
    });

    const { pile, aside } = rebuildPile(pub, seats, this.now());

    const state: GameState = {
      // The departed host's deal seed is gone with them, and deliberately so — it was never public.
      // Nothing downstream needs it: the pile is rebuilt from the remainder, not re-dealt.
      seed: 0,
      modeId: pub.modeId,
      players: pub.players,
      seats,
      deck: pile,
      aside,
      open: pub.open.slice(),
      spec: pub.spec,
      turn: pub.turn,
      ply: pub.ply,
      phase: 'play',
      reason: null,
      winners: [],
      log: [...pub.log, { text: 'A new hand takes the papers. The hearing continues.', kind: 'info' }],
      events: [],
    };
    // The turn may have belonged to somebody who has gone; hand it to the next live seat.
    if (!state.seats.some((x) => x.id === state.turn && x.alive)) {
      state.turn = state.seats.find((x) => x.alive)?.id ?? state.turn;
    }
    this.state = state;
    this.lastActor = state.turn;
    this.actorSince = this.now();
    this.botAt = this.now() + this.botDelay;
    // A seat that never attested is out, which may have ended the hearing outright. Note the seats
    // were built with `alive: s.alive && !missing`, so they are ALREADY dead here and forfeit() is a
    // no-op on them — which is exactly why the collapse has to be checked explicitly rather than
    // left to advance() to notice.
    for (const s of pub.seats) {
      if (this.state && !attested.has(s.id) && s.id !== this.selfId && s.alive) {
        this.state = forfeit(this.state, s.id);
      }
    }
    if (this.state) this.state = closeIfCollapsed(this.state);
    this.publish();
  }

  // ── the clock ───────────────────────────────────────────────────────────────

  tick(): void {
    if (this.awaitingHands && this.now() >= this.attestDeadline) {
      this.assumeControl();
      return;
    }
    if (!this.isHost || !this.state || this.state.phase !== 'play') return;

    // Anybody in the gallery who is a bot backs somebody, so a solo table behaves like a real one.
    if (this.solo) this.runGalleryBots();

    const actor = seatOf(this.state, this.state.turn);
    if (!actor) return;
    const isBot = this.solo ? actor.id !== this.selfId : false;
    if (isBot) {
      if (this.now() >= this.botAt) this.playFor(actor.id, this.botTier);
      return;
    }
    // A networked human who has gone quiet must not hold the room for ever.
    if (!this.solo && this.now() - this.actorSince >= this.decisionMs) this.playFor(actor.id, FILL_TIER);
  }

  private runGalleryBots(): void {
    if (!this.state) return;
    for (const seat of this.state.seats) {
      if (seat.id === this.selfId || !canBack(this.state, seat.id)) continue;
      const pub = this.snapshot();
      const priv = privateView(this.state, seat.id);
      if (!priv) continue;
      const pick = chooseBack(pub, priv, seat.id, tierOf(this.botTier));
      if (pick) {
        const next = back(this.state, seat.id, pick);
        if (next !== this.state) {
          this.state = next;
          this.publish();
        }
      }
    }
  }

  /** Play one seat's turn with the bot. Public so a test can drive a takeover with no network. */
  playFor(id: string, tier: TierName): boolean {
    if (!this.state || this.state.phase !== 'play' || this.state.turn !== id) return false;
    const pub = this.snapshot();
    const priv = privateView(this.state, id);
    if (!priv) return false;
    const move = choose(pub, priv, id, tierOf(tier), this.state.ply);
    if (!move) {
      // No legal move at all should be impossible — a seat on turn always holds cards. If it ever
      // happens, forfeit rather than stall the hearing for ever.
      this.state = forfeit(this.state, id);
      this.afterChange();
      return false;
    }
    const before = this.state;
    this.state = play(this.state, id, { cardId: move.cardId, target: move.target, named: move.named });
    if (this.state === before) {
      this.state = forfeit(this.state, id);
    }
    this.afterChange();
    return true;
  }

  // ── publishing ──────────────────────────────────────────────────────────────

  private snapshot(): PublicState {
    return publicView(this.state!, {
      standing: this.opts.standing,
      round: this.opts.round,
      target: this.mode.targetFor(this.state!.players),
    });
  }

  private publish(): void {
    if (!this.state) return;
    const pub = this.snapshot();
    this.pub = pub;
    this.opts.onPublic(pub);
    this.tx.sendSnap(pub);

    const mine = privateView(this.state, this.selfId);
    if (mine) {
      this.priv = mine;
      this.opts.onPrivate(mine);
    }
    for (const seat of this.state.seats) {
      if (seat.id === this.selfId) continue;
      if (this.solo) continue;
      const p = privateView(this.state, seat.id);
      if (p) this.tx.sendPriv(seat.id, p);
    }
    this.checkEnd(pub);
  }

  /** Fires once, on host and guest alike, so EVERY player reaches the summary. */
  private checkEnd(pub: PublicState): void {
    if (this.ended || pub.phase !== 'over') return;
    this.ended = true;
    const finished = this.state;
    this.opts.onRoundOver?.(finished ? awards(finished, this.mode) : awardsFromPublic(pub, this.mode));
  }

  // ── introspection ───────────────────────────────────────────────────────────

  hosting(): boolean {
    return this.isHost;
  }
  publicState(): PublicState | null {
    return this.pub;
  }
  privateState(): PrivateView | null {
    return this.priv;
  }
  authoritative(): GameState | null {
    return this.state;
  }
  /** What this seat may legally play right now, from whichever view it has. */
  myPlayable(): Card[] {
    if (this.state) return playableCards(this.state, this.selfId);
    if (!this.pub || !this.priv) return [];
    return playableFrom(this.pub, this.priv.hand, this.selfId);
  }
  /** Ms left before a silent player's move is made for them, or null when it does not apply. */
  deadlineMs(): number | null {
    if (this.solo || !this.state || this.state.phase !== 'play') return null;
    return Math.max(0, this.decisionMs - (this.now() - this.actorSince));
  }
}

/**
 * The pile, rebuilt as the remainder of a public composition.
 *
 * Anything that is not on the table and not in somebody's attested hand is, by arithmetic, still to
 * be drawn. Its order was secret from everyone including the departed host, so it is re-shuffled
 * from a fresh seed: unobservable by construction, and the only alternative is ending the hearing.
 */
export function rebuildPile(
  pub: PublicState,
  seats: readonly { hand: Card[] }[],
  saltMs: number,
): { pile: Card[]; aside: Card | null } {
  const left = pub.spec.slice();
  for (const s of pub.seats) for (const r of s.discards) left[r] = Math.max(0, (left[r] ?? 0) - 1);
  for (const r of pub.open) left[r] = Math.max(0, (left[r] ?? 0) - 1);
  for (const s of seats) for (const c of s.hand) left[c.rank] = Math.max(0, (left[c.rank] ?? 0) - 1);

  const ranks: number[] = [];
  for (let rank = 1; rank < left.length; rank++) for (let k = 0; k < (left[rank] ?? 0); k++) ranks.push(rank);

  // Ids must not collide with cards already in a hand, so they start above anything ever dealt.
  const base = specSize(pub.spec) + 1;
  const order = shuffle(makeRng(`takeover:${pub.round}:${pub.ply}:${saltMs}`), ranks);
  const cards: Card[] = order.map((rank, i) => ({ id: base + i, rank }));
  const aside = pub.asideHeld ? (cards.pop() ?? null) : null;
  return { pile: cards, aside };
}

/** Scoring from a public snapshot, for a guest whose host vanished at the close. */
function awardsFromPublic(pub: PublicState, mode: Mode): Award[] {
  const close = [...pub.events].reverse().find((e) => e.t === 'close');
  const held = new Map<string, number[]>();
  if (close && close.t === 'close') for (const h of close.held) held.set(h.id, h.ranks);
  const winners = new Set(pub.winners);
  return pub.seats.map((s) => {
    const ranks = held.get(s.id) ?? [];
    const top = s.alive ? ranks.reduce((a, r) => Math.max(a, r), 0) : 0;
    const won = winners.has(s.id);
    const backedRight = s.backed !== null && winners.has(s.backed);
    let gained = 0;
    if (mode.scoring === 'win') gained += won ? (pub.reason === 'last' ? mode.lastAward : mode.deckAward) : 0;
    else gained += top;
    if (backedRight) gained += mode.galleryAward;
    return { id: s.id, gained, won, held: top, backedRight };
  });
}
